import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Folder, ExternalLink, LayoutGrid, FileSignature, Scale, Calculator, Clock, CheckCircle2, X, Loader2, AlertCircle, Users, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const ProjectDetail = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [documents, setDocuments] = useState({ sows: [], legal_docs: [], estimates: [] });
  const [mondayItems, setMondayItems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDeliverables, setEditingDeliverables] = useState(false);
  const [delForm, setDelForm] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [projRes, delRes, docsRes, actRes] = await Promise.all([
        authFetch(`${API}/api/projects/${projectId}`),
        authFetch(`${API}/api/projects/${projectId}/deliverables`),
        authFetch(`${API}/api/projects/${projectId}/documents`),
        authFetch(`${API}/api/projects/${projectId}/activity`),
      ]);
      if (projRes.ok) {
        const p = (await projRes.json()).project;
        setProject(p);
        if (p.client_id) {
          try {
            const usersRes = await authFetch(`${API}/api/users`);
            if (usersRes.ok) setClient((await usersRes.json()).users?.find(u => u.id === p.client_id));
          } catch (e) { console.error('Failed to load client:', e); }
        }
        if (p.monday_board_id) {
          try {
            const mRes = await authFetch(`${API}/api/monday/boards/${p.monday_board_id}/items`);
            if (mRes.ok) setMondayItems((await mRes.json()).items || []);
          } catch (e) { console.error('Failed to load Monday items:', e); }
        }
      }
      if (delRes.ok) setDeliverables((await delRes.json()).deliverables || []);
      if (docsRes.ok) {
        const d = await docsRes.json();
        setDocuments({ sows: d.sows || [], legal_docs: d.legal_docs || [], estimates: d.estimates || [] });
      }
      if (actRes.ok) setActivities((await actRes.json()).activities || []);
    } catch (e) { toast.error('Failed to load project data'); }
    setLoading(false);
     
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveDeliverables = async () => {
    const res = await authFetch(`${API}/api/projects/${projectId}/deliverables`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ deliverables: delForm.filter(d => d.name.trim()) })
    });
    if (res.ok) { setDeliverables((await res.json()).deliverables); setEditingDeliverables(false); toast.success('Deliverables updated'); }
  };

  const startEditDeliverables = () => { setDelForm([...deliverables]); setEditingDeliverables(true); };

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch (e) { return d; }
  };

  const timeAgo = (d) => {
    if (!d) return '';
    try {
      const diff = Date.now() - new Date(d).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    } catch (e) { return ''; }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-brand" size={28} /></div>;

  if (!project) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="mx-auto text-text-muted mb-4" size={48} />
        <h2 className="text-xl font-semibold text-text-primary mb-2">Project not found</h2>
        <Link to="/internal/projects"><Button variant="outline">Back to projects</Button></Link>
      </div>
    );
  }

  const totalDocs = documents.sows.length + documents.legal_docs.length + documents.estimates.length;

  return (
    <div className="space-y-6" data-testid="project-detail">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-brand transition-colors" data-testid="back-button">
        <ArrowLeft size={16} /> Back to projects
      </button>

      {/* Header */}
      <header>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-3xl font-semibold tracking-tight text-text-primary" data-testid="project-name">{project.name}</h1>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${project.status === 'Active' ? 'bg-success/10 text-success' : 'bg-surface-page text-text-secondary'}`}>
            {project.status}
          </span>
          <button
            onClick={() => {
              const url = `${window.location.origin}/internal/projects/${project.id}`;
              const prefill = `Sharing this project — take a look:\n*${project.name}*${client?.name ? ` _(${client.name})_` : ''}\n${url}`;
              window.dispatchEvent(new CustomEvent('ventas:slack:dm', {
                detail: { prefillText: prefill },
              }));
            }}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-chart-purple/10 text-chart-purple hover:bg-chart-purple/20 text-sm font-medium transition-colors"
            data-testid="project-share-slack"
            title="Send this project's link to a teammate via Slack"
          >
            <MessageSquare size={14} /> Share via Slack
          </button>
        </div>
        <div className="flex items-center gap-4 text-sm text-text-secondary">
          {client && <span>Client: <strong className="text-text-primary">{client.name}</strong></span>}
          <span>Created {formatDate(project.created_at)}</span>
        </div>
      </header>

      {/* Integration badges */}
      <div className="flex items-center gap-3 flex-wrap">
        {project.drive_folder_id && (
          <a href={`https://drive.google.com/drive/folders/${project.drive_folder_id}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-border-subtle text-sm text-text-secondary hover:border-brand/30 transition-colors shadow-xs" data-testid="drive-link">
            <Folder size={14} className="text-brand" /> Google Drive <ExternalLink size={12} />
          </a>
        )}
        {project.monday_board_id && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-border-subtle text-sm text-text-secondary shadow-xs" data-testid="monday-badge">
            <LayoutGrid size={14} className="text-chart-purple" /> Monday board linked
          </span>
        )}
      </div>

      {/* Stats row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-md border border-border-subtle bg-white p-5 shadow-xs">
          <div className="text-sm text-text-secondary">Deliverables</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight">{deliverables.length}</div>
        </div>
        <div className="rounded-md border border-border-subtle bg-white p-5 shadow-xs">
          <div className="text-sm text-text-secondary">SOWs</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight">{documents.sows.length}</div>
        </div>
        <div className="rounded-md border border-border-subtle bg-white p-5 shadow-xs">
          <div className="text-sm text-text-secondary">Legal docs</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight">{documents.legal_docs.length}</div>
        </div>
        <div className="rounded-md border border-border-subtle bg-white p-5 shadow-xs">
          <div className="text-sm text-text-secondary">Estimates</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight">{documents.estimates.length}</div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left (2/3): Deliverables + Documents */}
        <div className="lg:col-span-2 space-y-6">

          {/* Deliverables */}
          <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="deliverables-section">
            <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary">Deliverables</h3>
              {!editingDeliverables ? (
                <Button variant="outline" size="sm" onClick={startEditDeliverables} className="border-border-subtle text-text-secondary hover:text-brand hover:border-brand/30" data-testid="edit-deliverables-btn">Edit</Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingDeliverables(false)} className="border-border-subtle">Cancel</Button>
                  <Button size="sm" className="bg-brand text-white hover:bg-brand-hover shadow-xs" onClick={saveDeliverables} data-testid="save-deliverables-btn">Save</Button>
                </div>
              )}
            </div>
            <div className="p-5">
              {editingDeliverables ? (
                <div className="space-y-2">
                  {delForm.map((d, i) => (
                    <div key={`item-${i}`} className="flex gap-2">
                      <Input value={d.name} onChange={e => { const c = [...delForm]; c[i] = { ...c[i], name: e.target.value }; setDelForm(c); }} placeholder="Deliverable name" className="flex-1 border-border-subtle" />
                      <Input value={d.description || ''} onChange={e => { const c = [...delForm]; c[i] = { ...c[i], description: e.target.value }; setDelForm(c); }} placeholder="Description" className="flex-1 border-border-subtle" />
                      <button onClick={() => setDelForm(delForm.filter((_, j) => j !== i))} className="text-text-muted hover:text-error transition-colors"><X size={16} /></button>
                    </div>
                  ))}
                  <button onClick={() => setDelForm([...delForm, { name: '', description: '' }])} className="text-xs font-medium text-brand hover:text-brand-hover">+ Add deliverable</button>
                </div>
              ) : deliverables.length === 0 ? (
                <p className="text-sm text-text-muted py-4 text-center">No deliverables defined yet</p>
              ) : (
                <div className="space-y-2">
                  {deliverables.map((d, i) => (
                    <div key={`item-${i}`} className="flex items-start gap-3 p-3 rounded-md bg-surface-page">
                      <CheckCircle2 size={16} className="text-success mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-text-primary">{d.name}</p>
                        {d.description && <p className="text-xs text-text-tertiary mt-0.5">{d.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Milestones (from template) */}
          {project.milestones && project.milestones.length > 0 && (
            <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="milestones-section">
              <div className="border-b border-border-subtle px-5 py-4">
                <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2"><Clock size={14} className="text-brand" /> Milestones</h3>
              </div>
              <div className="p-5">
                <div className="relative">
                  {project.milestones.map((m, i) => (
                    <div key={`item-${i}`} className="flex gap-4 pb-4 last:pb-0" data-testid={`milestone-${i}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full border-2 shrink-0 ${m.status === 'complete' ? 'bg-success border-success' : 'bg-white border-brand'}`} />
                        {i < project.milestones.length - 1 && <div className="w-px flex-1 bg-border-subtle mt-1" />}
                      </div>
                      <div className="flex-1 -mt-1">
                        <p className="text-sm font-medium text-text-primary">{m.name}</p>
                        <p className="text-xs text-text-tertiary mt-0.5">{m.description}</p>
                        <span className="text-xs text-text-muted">Week {m.week_offset}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Resource Assignments (from template) */}
          {project.resource_assignments && project.resource_assignments.length > 0 && (
            <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="resources-section">
              <div className="border-b border-border-subtle px-5 py-4">
                <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2"><Users size={14} className="text-brand" /> Team Allocation</h3>
              </div>
              <div className="p-5 space-y-3">
                {project.resource_assignments.map((r, i) => (
                  <div key={`item-${i}`} className="flex items-center justify-between" data-testid={`resource-${i}`}>
                    <span className="text-sm text-text-primary">{r.role}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-24 h-2 rounded-full bg-surface-page overflow-hidden">
                        <div className="h-full rounded-full bg-brand" style={{ width: `${r.allocation_pct}%` }} />
                      </div>
                      <span className="text-xs font-medium text-text-secondary w-8 text-right">{r.allocation_pct}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Documents */}
          <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="documents-section">

            <div className="border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary">Documents ({totalDocs})</h3>
            </div>
            <div className="p-5">
              {totalDocs === 0 ? (
                <p className="text-sm text-text-muted py-4 text-center">No documents linked to this project yet</p>
              ) : (
                <div className="space-y-2">
                  {documents.sows.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-3 rounded-md bg-surface-page hover:bg-white hover:shadow-xs hover:border-border-subtle border border-transparent cursor-pointer transition-all duration-200 ease-productive"
                      onClick={() => navigate('/internal/client-management/sow')} data-testid={`doc-sow-${s.id?.slice(0,8)}`}>
                      <div className="flex items-center gap-3">
                        <FileSignature size={16} className="text-brand" />
                        <div>
                          <p className="text-sm font-medium text-text-primary">{s.project_name || s.client_name} — SOW</p>
                          <p className="text-xs text-text-tertiary">{s.template_type} · {s.status} · {formatDate(s.created_at)}</p>
                        </div>
                      </div>
                      {s.total_value && <span className="text-sm font-semibold text-brand">${Number(s.total_value).toLocaleString()}</span>}
                    </div>
                  ))}
                  {documents.legal_docs.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-3 rounded-md bg-surface-page hover:bg-white hover:shadow-xs hover:border-border-subtle border border-transparent cursor-pointer transition-all duration-200 ease-productive"
                      onClick={() => navigate('/internal/client-management/legal')} data-testid={`doc-legal-${d.id?.slice(0,8)}`}>
                      <div className="flex items-center gap-3">
                        <Scale size={16} className="text-warning" />
                        <div>
                          <p className="text-sm font-medium text-text-primary">{d.client_name} — {(d.doc_type || '').toUpperCase()}</p>
                          <p className="text-xs text-text-tertiary">{d.status} · {formatDate(d.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {documents.estimates.map(e => (
                    <div key={e.id} className="flex items-center justify-between p-3 rounded-md bg-surface-page hover:bg-white hover:shadow-xs hover:border-border-subtle border border-transparent cursor-pointer transition-all duration-200 ease-productive"
                      onClick={() => navigate('/internal/client-management/estimators')} data-testid={`doc-est-${e.id?.slice(0,8)}`}>
                      <div className="flex items-center gap-3">
                        <Calculator size={16} className="text-success" />
                        <div>
                          <p className="text-sm font-medium text-text-primary">{e.project_name || e.client_name} — Estimate</p>
                          <p className="text-xs text-text-tertiary">{e.status} · {formatDate(e.created_at)}</p>
                        </div>
                      </div>
                      {e.total_cost != null && <span className="text-sm font-semibold text-success">${Number(e.total_cost).toLocaleString()}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Monday Tasks */}
          {project.monday_board_id && (
            <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="monday-tasks-section">
              <div className="border-b border-border-subtle px-5 py-4">
                <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                  <LayoutGrid size={14} className="text-chart-purple" /> Monday.com tasks
                </h3>
              </div>
              <div className="p-5">
                {mondayItems.length === 0 ? (
                  <p className="text-sm text-text-muted py-4 text-center">No tasks found on the linked board</p>
                ) : (
                  <div className="space-y-1">
                    {mondayItems.slice(0, 10).map((item, i) => (
                      <div key={item.id || i} className="flex items-center justify-between p-3 rounded-md bg-surface-page">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${item.state === 'done' ? 'bg-success' : item.state === 'active' ? 'bg-info' : 'bg-text-muted'}`} />
                          <span className="text-sm font-medium text-text-primary">{item.name}</span>
                        </div>
                        {item.column_values?.find(c => c.title === 'Status') && (
                          <span className="text-xs text-text-tertiary">{item.column_values.find(c => c.title === 'Status').text}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}
        </div>

        {/* Right (1/3): Activity + Info */}
        <div className="space-y-6">
          <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="activity-section">
            <div className="border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary">Recent activity</h3>
            </div>
            <div className="p-5">
              {activities.length === 0 ? (
                <p className="text-sm text-text-muted py-4 text-center">No activity recorded yet</p>
              ) : (
                <div className="space-y-0">
                  {activities.slice(0, 15).map((a, i) => (
                    <div key={a.id || i} className="flex gap-3 py-3 border-b border-border-subtle last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand mt-2 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-primary truncate">{a.description}</p>
                        <p className="text-xs text-text-muted mt-0.5">{a.user_name} · {timeAgo(a.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="rounded-md border border-border-subtle bg-white shadow-xs" data-testid="project-info-card">
            <div className="border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary">Project info</h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div><span className="text-text-tertiary">ID:</span> <span className="font-mono text-xs text-text-secondary">{project.id?.slice(0, 8)}</span></div>
              {client && <div><span className="text-text-tertiary">Client:</span> <span className="font-medium text-text-primary">{client.name}</span></div>}
              {client?.email && <div><span className="text-text-tertiary">Email:</span> <span className="text-text-secondary">{client.email}</span></div>}
              <div><span className="text-text-tertiary">Status:</span> <span className="font-medium text-text-primary">{project.status}</span></div>
              {project.template_id && <div><span className="text-text-tertiary">Template:</span> <span className="font-medium text-brand">Pre-configured</span></div>}
              <div><span className="text-text-tertiary">Created:</span> <span className="text-text-secondary">{formatDate(project.created_at)}</span></div>
              {project.drive_folder_id && <div><span className="text-text-tertiary">Drive:</span> <a href={`https://drive.google.com/drive/folders/${project.drive_folder_id}`} target="_blank" rel="noreferrer" className="text-brand hover:text-brand-hover">Open folder</a></div>}
              {project.monday_board_id && <div><span className="text-text-tertiary">Monday ID:</span> <span className="font-mono text-xs text-text-secondary">{project.monday_board_id}</span></div>}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
